import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Coffee, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useBranchAuth } from '../context/BranchAuthContext';
import { useToast } from '../hooks/use-toast';

/**
 * BranchDeskTrackerLogin — Feb 2026
 *
 * Dedicated mobile-first sign-in for the branch Desk Tracker page.
 * Verifies the branch username + password (same credentials as the
 * standard `/branch/login`) BUT:
 *   • Skips the duty-hours gate — desk leaving needs 24/7 access.
 *   • Issues a `scope="desk"` JWT that can only reach the
 *     `/branch/desk/*` endpoints. All other branch actions remain
 *     locked to the normal `/branch/login` flow.
 * Post-login → `/branch/desk-tracker`.
 */
const BranchDeskTrackerLogin = () => {
  const { deskToken, deskLogin, loading } = useBranchAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => { document.title = 'Desk Tracker — Sign In'; }, []);

  if (deskToken) return <Navigate to="/branch/desk-tracker" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast({ title: 'Enter username and password' });
      return;
    }
    const res = await deskLogin(username.trim(), password);
    if (res.ok) {
      toast({ title: 'Welcome', description: 'Desk Tracker unlocked.' });
      navigate('/branch/desk-tracker', { replace: true });
    } else {
      toast({ title: 'Sign-in failed', description: res.error || 'Invalid username or password' });
    }
  };

  return (
    <div
      className="min-h-[100dvh] bg-neutral-50 flex flex-col"
      data-testid="branch-desk-login-page"
    >
      <header className="px-4 pt-6 pb-2 flex flex-col items-center">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-3">
          <Coffee className="w-7 h-7 text-amber-700" />
        </div>
        <h1 className="text-xl font-bold text-neutral-900" data-testid="branch-desk-login-title">
          Desk Tracker
        </h1>
        <p className="text-xs text-neutral-500 mt-1 text-center max-w-xs">
          Branch sign-in for logging desk breaks. Available any time — no duty hours needed.
        </p>
      </header>

      <form
        onSubmit={submit}
        className="flex-1 px-5 pt-6 pb-8 flex flex-col justify-center gap-4"
        data-testid="branch-desk-login-form"
      >
        <label className="block">
          <span className="text-xs font-semibold text-neutral-700 mb-1 block">Branch username</span>
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              autoCapitalize="off"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="branch01"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-neutral-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-base"
              data-testid="branch-desk-login-username"
            />
          </div>
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-neutral-700 mb-1 block">Password</span>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type={showPwd ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full h-12 pl-10 pr-11 rounded-xl border border-neutral-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 text-base"
              data-testid="branch-desk-login-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-neutral-500 hover:bg-neutral-100 flex items-center justify-center"
              aria-label={showPwd ? 'Hide password' : 'Show password'}
              data-testid="branch-desk-login-toggle-password"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="mt-2 w-full min-h-[56px] rounded-2xl bg-amber-600 hover:bg-amber-700 active:bg-amber-800 disabled:opacity-60 text-white font-bold text-lg shadow-md flex items-center justify-center gap-2 transition"
          data-testid="branch-desk-login-submit"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Coffee className="w-5 h-5" />}
          Sign in for Desk Tracker
        </button>

        <p className="text-center text-[11px] text-neutral-500 mt-2">
          Need full branch access? Use the{' '}
          <button
            type="button"
            onClick={() => navigate('/branch/login')}
            className="underline text-neutral-700 hover:text-neutral-900"
            data-testid="branch-desk-login-goto-full"
          >
            standard branch sign-in
          </button>.
        </p>
      </form>
    </div>
  );
};

export default BranchDeskTrackerLogin;
